import { Box, Typography } from '@mui/material';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { IdField } from '~/components/Fields';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import { dicomViewerAtom } from '~/state/atoms/viewerAtoms';
import { formatSeriesDate } from '~/utils/date';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';
import { formatCm, mmToCm } from '~/utils/units';
import PropertiesPanel from './PropertiesPanel';

interface ImagePropertiesProps {
    image: Image3D;
    hasSharedFrameOfReference?: boolean;
    onDelete?: () => void;
}

export default function ImageProperties({
    image,
    hasSharedFrameOfReference,
    onDelete,
}: ImagePropertiesProps) {
    const intl = useIntl();
    const setDicomViewer = useSetRecoilState(dicomViewerAtom);
    const {
        frameOfReferenceUid,
        modality,
        length,
        series,
        rows,
        cols,
        manufacturer,
        manufacturerModelName,
        modalitySpecificAttributes,
        pixelSpacing,
        fieldOfViewMm,
        imageOrientation,
        patientPosition,
        stationName,
        filterType,
        imageType,
        hasInterpolatedSlices,
        interpolatedSliceCount,
    } = transform3DImageData(image);

    const dash = '\u2014';

    const datasetWrapper = image.firstSlice?.datasetWrapper;
    const onViewDicom = useCallback(() => {
        if (!datasetWrapper) {
            return;
        }
        setDicomViewer({
            dataset: datasetWrapper,
            title: series.description || 'Image',
        });
    }, [datasetWrapper, series.description, setDicomViewer]);

    return (
        <PropertiesPanel
            onViewDicom={datasetWrapper ? onViewDicom : undefined}
            onDelete={onDelete}
            subtitle={
                hasSharedFrameOfReference ? (
                    <Typography
                        fontSize={11}
                        color="text.disabled"
                        mb={1}
                        mt={-0.5}
                    >
                        <FormattedMessage id="workflow.imageRegistration.sameFOR" />
                    </Typography>
                ) : undefined
            }
        >
            <LabelText>
                <FormattedMessage id="imageType" />
            </LabelText>
            <ValueText component="span">
                {imageType.length > 0 ? imageType.join(', ') : dash}
            </ValueText>

            <LabelText>
                <FormattedMessage id="seriesDescription" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>
                    {series.description || dash}
                </SensitiveInfoTypography>
            </ValueText>

            <LabelText>
                <FormattedMessage id="seriesDate" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>
                    {formatSeriesDate(series?.date) || dash}
                </SensitiveInfoTypography>
            </ValueText>

            <LabelText>
                <FormattedMessage id="modality" />
            </LabelText>
            <ValueText component="span">{modality || dash}</ValueText>

            <LabelText>
                <FormattedMessage id="sliceThickness" /> (cm)
            </LabelText>
            <ValueText component="span">
                {pixelSpacing?.[2] != null
                    ? formatCm(mmToCm(pixelSpacing[2]))
                    : dash}
            </ValueText>

            <LabelText>
                <FormattedMessage id="matrix" />
            </LabelText>
            <ValueText component="span">
                {`${cols} x ${rows} x ${length}`}
            </ValueText>

            {hasInterpolatedSlices && (
                <>
                    <LabelText>
                        <FormattedMessage id="image.interpolatedSlices.label" />
                    </LabelText>
                    <ValueText component="span" sx={{ color: 'warning.main' }}>
                        <FormattedMessage
                            id="image.interpolatedSlices.value"
                            values={{
                                count: interpolatedSliceCount,
                                total: length,
                            }}
                        />
                    </ValueText>
                </>
            )}

            <LabelText>
                <FormattedMessage id="resolution" /> (cm)
            </LabelText>
            <ValueText component="span">
                {pixelSpacing
                    ? pixelSpacing
                          .map((x) => formatCm(mmToCm(x), 3))
                          .join(' x ')
                    : dash}
            </ValueText>

            <LabelText>
                <FormattedMessage id="fieldOfView" /> (cm)
            </LabelText>
            <ValueText component="span">
                {fieldOfViewMm.map((v) => formatCm(mmToCm(v))).join(' x ')}
            </ValueText>

            <LabelText>
                <FormattedMessage id="manufacturer" />
            </LabelText>
            <ValueText component="span">{manufacturer || dash}</ValueText>

            <LabelText>
                <FormattedMessage id="manufacturerModelName" />
            </LabelText>
            <ValueText component="span">
                {manufacturerModelName || dash}
            </ValueText>

            <LabelText>
                <FormattedMessage id="imageOrientation" />
            </LabelText>
            <ValueText component="span">
                {imageOrientation ? (
                    <Box display="inline-flex" gap={0.5}>
                        {imageOrientation.map((axis, index) => (
                            <span key={index}>
                                [
                                {axis
                                    .map((coord) =>
                                        Math.abs(coord) == 1 || coord == 0
                                            ? coord
                                            : coord.toFixed(2)
                                    )
                                    .join(',')}
                                ]
                            </span>
                        ))}
                    </Box>
                ) : (
                    dash
                )}
            </ValueText>

            <LabelText>
                <FormattedMessage id="patientPosition" />
            </LabelText>
            <ValueText component="span">{patientPosition || dash}</ValueText>

            <LabelText>
                <FormattedMessage id="stationName" />
            </LabelText>
            <ValueText component="span">{stationName || dash}</ValueText>

            <LabelText>
                <FormattedMessage id="filterType" />
            </LabelText>
            <ValueText component="span">{filterType || dash}</ValueText>

            {modality === Modality.CT && (
                <>
                    <LabelText>
                        <FormattedMessage id="kvp" />
                    </LabelText>
                    <ValueText component="span">
                        {modalitySpecificAttributes?.kvp || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="ctdi" />
                    </LabelText>
                    <ValueText component="span">
                        {modalitySpecificAttributes?.ctdi || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="convolutionKernel" />
                    </LabelText>
                    <ValueText component="span">
                        {modalitySpecificAttributes?.kernel || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="protocolName" />
                    </LabelText>
                    <ValueText component="span">
                        {modalitySpecificAttributes?.protocolName || dash}
                    </ValueText>
                </>
            )}

            {modality === Modality.MR && (
                <>
                    <LabelText>
                        <FormattedMessage id="scanningSequence" />
                    </LabelText>
                    <ValueText component="span">
                        {modalitySpecificAttributes?.scanningSequence || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="mrAcquisitionType" />
                    </LabelText>
                    <ValueText component="span">
                        {modalitySpecificAttributes?.mraAcquisitionType || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="sequenceVariant" />
                    </LabelText>
                    <ValueText component="span">
                        {modalitySpecificAttributes?.sequenceVariant || dash}
                    </ValueText>
                </>
            )}

            <LabelText>
                {intl.formatMessage({ id: 'seriesInstanceUid' })}
            </LabelText>
            <IdField id={series.id} typographyProps={{ fontSize: 11 }} />

            <LabelText>
                {intl.formatMessage({ id: 'frameOfReferenceUid' })}
            </LabelText>
            <IdField
                id={frameOfReferenceUid}
                typographyProps={{ fontSize: 11 }}
            />
        </PropertiesPanel>
    );
}
